import { useMemo, useState } from "react";

/**
 * GitHub-style submission heatmap.
 * - 12 weeks (~84 days) by default, 7 rows (Mon-Sun) × N cols.
 * - Color buckets: 0, 1-2, 3-5, 6-10, 11+.
 * - Hover for a tooltip with the exact day + count + sample submitters.
 *
 * Props:
 *   submissions: Array<{ submitted_at: string, submitted_by_name?: string }>
 *   weeks: number (default 12)
 */
export default function SubmissionHeatmap({ submissions, weeks = 12 }) {
  const { cells, total, peak, daysActive, peakDay } = useMemo(() => {
    const today = startOfDay(new Date());
    // Go back to the most recent Sunday so the rightmost column is "this week",
    // with Sun on top → Sat on bottom (GitHub-style 7 rows).
    const lastDayInGrid = endOfWeek(today);
    const totalDays = weeks * 7;
    const firstDayInGrid = addDays(lastDayInGrid, -(totalDays - 1));

    // Bucket submissions by ISO date string for O(1) lookup.
    const buckets = new Map();
    const submittersByDay = new Map();
    let totalCount = 0;
    for (const s of submissions || []) {
      const d = parseDate(s.submitted_at);
      if (!d) continue;
      const day = startOfDay(d);
      if (day < firstDayInGrid || day > lastDayInGrid) continue;
      const key = isoDay(day);
      buckets.set(key, (buckets.get(key) || 0) + 1);
      const arr = submittersByDay.get(key) || [];
      const who = s.submitted_by_name || s.submitted_by_email || "—";
      if (!arr.includes(who) && arr.length < 5) arr.push(who);
      submittersByDay.set(key, arr);
      totalCount += 1;
    }

    // Build the grid as an array of { date, count, samples } in display order.
    const cells = [];
    let peak = 0;
    let peakDayKey = null;
    let active = 0;
    let cursor = new Date(firstDayInGrid);
    while (cursor <= lastDayInGrid) {
      const key = isoDay(cursor);
      const count = buckets.get(key) || 0;
      if (count > 0) active += 1;
      if (count > peak) { peak = count; peakDayKey = key; }
      cells.push({ date: new Date(cursor), key, count, samples: submittersByDay.get(key) || [] });
      cursor = addDays(cursor, 1);
    }
    return {
      cells,
      total: totalCount,
      peak,
      peakDay: peakDayKey,
      daysActive: active,
    };
  }, [submissions, weeks]);

  const [hover, setHover] = useState(null);

  const colorFor = (n) => {
    if (n === 0) return "bg-slate-100";
    if (n <= 2) return "bg-emerald-200";
    if (n <= 5) return "bg-emerald-400";
    if (n <= 10) return "bg-emerald-600";
    return "bg-emerald-800";
  };

  // Reshape the cells into 7 rows × `weeks` cols (Sunday on top).
  const rows = Array.from({ length: 7 }, () => []);
  cells.forEach((c, idx) => {
    const row = idx % 7;
    rows[row].push(c);
  });

  // Month labels — show a label only on the first column of each month.
  const monthLabelByCol = [];
  for (let col = 0; col < weeks; col += 1) {
    const colCells = cells.slice(col * 7, col * 7 + 7);
    const top = colCells[0];
    if (!top) { monthLabelByCol.push(""); continue; }
    if (col === 0 || top.date.getDate() <= 7) {
      monthLabelByCol.push(top.date.toLocaleString(undefined, { month: "short" }));
    } else {
      monthLabelByCol.push("");
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="submission-heatmap">
      <div className="flex items-baseline justify-between mb-2">
        <p className="label-overline">Submission heatmap · last {weeks} weeks</p>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span><strong className="text-slate-700 tabular">{total}</strong> total</span>
          <span><strong className="text-slate-700 tabular">{daysActive}</strong> active days</span>
          <span><strong className="text-slate-700 tabular">{peak}</strong> peak{peakDay ? ` · ${formatDay(parseISO(peakDay))}` : ""}</span>
        </div>
      </div>
      <div className="relative overflow-x-auto">
        <div className="inline-flex gap-2 items-start">
          {/* Day-of-week labels (Mon, Wed, Fri shown to keep it tidy) */}
          <div className="flex flex-col gap-[3px] mt-[14px] pr-1 text-[9px] text-slate-400 select-none">
            {["", "Mon", "", "Wed", "", "Fri", ""].map((l, i) => (
              <div key={i} style={{ height: 12 }} className="leading-none">{l}</div>
            ))}
          </div>
          <div className="flex flex-col gap-[3px]">
            {/* Month labels */}
            <div className="flex gap-[3px] text-[9px] text-slate-400 select-none">
              {monthLabelByCol.map((l, i) => (
                <div key={i} style={{ width: 12 }} className="leading-none">{l}</div>
              ))}
            </div>
            {rows.map((row, ri) => (
              <div key={ri} className="flex gap-[3px]">
                {row.map((c, ci) => (
                  <button
                    type="button"
                    key={c.key}
                    className={`w-3 h-3 rounded-[2px] ${colorFor(c.count)} hover:ring-2 hover:ring-emerald-300 transition`}
                    onMouseEnter={() => setHover(c)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(c)}
                    onBlur={() => setHover(null)}
                    aria-label={`${c.count} submissions on ${formatDay(c.date)}`}
                    data-testid={`heatmap-cell-${c.key}`}
                  />
                ))}
              </div>
            ))}
            {/* Legend */}
            <div className="flex items-center gap-1 mt-1 text-[9px] text-slate-400">
              <span>Less</span>
              {["bg-slate-100", "bg-emerald-200", "bg-emerald-400", "bg-emerald-600", "bg-emerald-800"].map((cls, i) => (
                <span key={i} className={`w-3 h-3 rounded-[2px] ${cls}`} />
              ))}
              <span>More</span>
            </div>
          </div>
        </div>
        {hover && (
          <div className="mt-2 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 inline-block" data-testid="heatmap-tooltip">
            <span className="font-medium">{formatDay(hover.date)}</span>
            <span className="mx-1.5 text-slate-400">·</span>
            <span className="tabular">{hover.count}</span> submission{hover.count === 1 ? "" : "s"}
            {hover.samples.length > 0 && (
              <span className="text-slate-500"> ({hover.samples.join(", ")}{hover.samples.length === 5 ? ", …" : ""})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Date helpers ---------------------------------------------------------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function endOfWeek(d) {
  // Saturday-end (so Sunday starts the column on top in our grid layout).
  const x = startOfDay(d);
  const dow = x.getDay(); // 0=Sun..6=Sat
  return addDays(x, 6 - dow);
}
function isoDay(d) {
  return startOfDay(d).toISOString().slice(0, 10);
}
function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}
function formatDay(d) {
  return d?.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
